<template lang="pug">
  div
    .mb-6
      h1.display-1.mb-2 Upload Card Component
      p.text--secondary A card component that handles file uploads with drag-and-drop support and customizable slots for icon, title, and description.

    // Upload Card Variations
    .component-section.mb-8#basic-variations
      h3.mb-4.cursor-pointer(@click="navigateToSection('basic-variations')") Basic Variations
      .variations-grid
        .variation-item.mb-6(v-for="variation in uploadVariations" :key="variation.name" :id="variation.id")
          .variation-header.mb-3
            h4.cursor-pointer(@click="navigateToSection(variation.id)") {{ variation.name }}
            p.text--secondary.caption {{ variation.description }}

          .variation-preview.mb-4.pa-4.border.rounded(style="max-width: 500px;")
            yz-upload-card(v-if="variation.id === 'basic-card'" v-bind="variation.props" v-model="basicCardFiles" @error="handleUploadError")
            yz-upload-card(v-else-if="variation.id === 'multiple-files'" v-bind="variation.props" v-model="multipleFilesFiles" @error="handleUploadError")
            yz-upload-card(v-else-if="variation.id === 'custom-styling'" v-bind="variation.props" v-model="customStylingFiles" @error="handleUploadError")
            yz-upload-card(v-else-if="variation.id === 'document-upload'" v-bind="variation.props" v-model="documentUploadFiles" @error="handleUploadError")
            yz-upload-card(v-else-if="variation.id === 'elevated-card'" v-bind="variation.props" v-model="elevatedCardFiles" @error="handleUploadError")
            yz-upload-card(v-else-if="variation.id === 'append-files'" v-bind="variation.props" v-model="appendFilesFiles" @error="handleUploadError")
            yz-upload-card(v-else-if="variation.id === 'custom-drag-colors'" v-bind="variation.props" v-model="customDragColorsFiles" @error="handleUploadError")
            yz-upload-card(v-else-if="variation.id === 'immediate-upload'" v-bind="variation.props" v-model="immediateUploadFiles" @upload-progress="handleUploadProgress" @error="handleUploadError" @upload-success="handleUploadSuccess" @upload-error="handleUploadError")

            template(v-if="variationFiles[variation.id] && variationFiles[variation.id].length > 0")
              .mt-4
                h5.mb-2 Uploaded Files:
                yz-file-preview(v-model="variationFiles[variation.id]")

          .variation-code
            .code-header.d-flex.align-center.mb-2
              yz-btn(icon-only size="md" :icon="copiedCode === variation.code ? 'mdi-check' : 'mdi-content-copy'" :icon-color="copiedCode === variation.code ? 'success' : ''" @click="copyCode(variation.code)")
              span.font-weight-medium.ml-2 Code

            pre.code-block.pa-3.rounded.text-caption(style="background-color: #f5f5f5; overflow-x: auto;")
              code {{ variation.code }}

            template(v-if="variationFiles[variation.id] && variationFiles[variation.id].length > 0")
              .mt-3
                .code-header.d-flex.align-center.mb-2
                  span.font-weight-medium Current v-model Data:
                pre.code-block.pa-3.rounded.text-caption(style="background-color: #e8f5e8; overflow-x: auto;")
                  code {{ formatModelData(variationFiles[variation.id]) }}

    // Slot Usage Examples
    .component-section.mb-8#slot-examples
      h3.mb-4.cursor-pointer(@click="navigateToSection('slot-examples')") Slot Usage Examples
      .variations-grid
        .variation-item.mb-6(v-for="example in slotExamples" :key="example.name" :id="example.id")
          .variation-header.mb-3
            h4.cursor-pointer(@click="navigateToSection(example.id)") {{ example.name }}
            p.text--secondary.caption {{ example.description }}

          .variation-preview.mb-4.pa-4.border.rounded(style="max-width: 500px;")
            yz-upload-card(v-if="example.id === 'custom-icon'" v-model="customIconFiles" @error="handleUploadError")
              template(#icon)
                yz-icon(size="48" color="success") mdi-image-multiple
              template(#title)
                .text-h6.text--primary Gallery Upload
              template(#description)
                .text-body-2.text--secondary Add images to your gallery

            yz-upload-card(v-else-if="example.id === 'profile-upload'" v-model="profileUploadFiles" @error="handleUploadError" accept="image/*" :multiple="false")
              template(#icon)
                yz-icon(size="56" color="primary") mdi-account-circle
              template(#title)
                .text-h6.text--primary Profile Picture
              template(#description)
                .text-body-2.text--secondary Upload your profile picture (PNG, JPG)

            yz-upload-card(v-else-if="example.id === 'document-card'" v-model="documentCardFiles" @error="handleUploadError" accept=".pdf,.doc,.docx")
              template(#icon)
                yz-icon(size="52" color="orange") mdi-file-document
              template(#title)
                .text-h6.text--primary Document Upload
              template(#description)
                .text-body-2.text--secondary Upload PDF, DOC, or DOCX files

            template(v-if="slotFiles[example.id] && slotFiles[example.id].length > 0")
              .mt-4
                h5.mb-2 Selected Files:
                yz-file-preview(v-model="slotFiles[example.id]" :show-actions="false")

          .variation-code
            .code-header.d-flex.align-center.mb-2
              yz-btn(icon-only size="md" :icon="copiedCode === example.code ? 'mdi-check' : 'mdi-content-copy'" :icon-color="copiedCode === example.code ? 'success' : ''" @click="copyCode(example.code)")
              span.font-weight-medium.ml-2 Code

            pre.code-block.pa-3.rounded.text-caption(style="background-color: #f5f5f5; overflow-x: auto;")
              code {{ example.code }}

    // Drag and Drop Features
    .component-section.mb-8#drag-drop-features
      h3.mb-4.cursor-pointer(@click="navigateToSection('drag-drop-features')") Drag and Drop Features
      .mb-4
        p The YzUploadCard component supports both click-to-upload and drag-and-drop functionality:
        ul.ml-4
          li Click anywhere on the card to open the file browser
          li Drag and drop files directly onto the card
          li Visual feedback with customizable colors during drag-over
          li Respects all file type and multiple file restrictions
          li Same processing pipeline for both input methods
      
      .code-example.mb-4
        h4.mb-2 Basic Drag and Drop Usage
        pre.code-block.pa-3.rounded.text-caption(style="background-color: #f5f5f5; overflow-x: auto;")
          code yz-upload-card(v-model="files" multiple)
      
      .code-example.mb-4
        h4.mb-2 Custom Drag Colors
        pre.code-block.pa-3.rounded.text-caption(style="background-color: #f5f5f5; overflow-x: auto;")
          code {{ customDragExample }}

    // API Documentation
    .component-section.mb-8#api
      h3.mb-4.cursor-pointer(@click="navigateToSection('api')") API
      .props-table
        v-simple-table
          template(v-slot:default)
            thead
              tr
                th Prop
                th Type
                th Default
                th Description
            tbody
              tr(v-for="prop in uploadCardProps" :key="prop.name")
                td.font-weight-medium.text-no-wrap {{ prop.name }}
                td.text--secondary.text-no-wrap {{ prop.type }}
                td.text--secondary.text-no-wrap {{ prop.default }}
                td.single-line {{ prop.description }}

    // Events Documentation
    .component-section.mb-8#events
      h3.mb-4.cursor-pointer(@click="navigateToSection('events')") Events
      .events-table
        v-simple-table
          template(v-slot:default)
            thead
              tr
                th Event
                th Payload
                th Description
            tbody
              tr(v-for="event in uploadCardEvents" :key="event.name")
                td.font-weight-medium.text-no-wrap {{ event.name }}
                td.text--secondary.text-no-wrap {{ event.payload }}
                td.single-line {{ event.description }}

    // Slots Documentation
    .component-section.mb-8#slots
      h3.mb-4.cursor-pointer(@click="navigateToSection('slots')") Slots
      .slots-table
        v-simple-table
          template(v-slot:default)
            thead
              tr
                th Slot
                th Description
            tbody
              tr(v-for="slot in uploadCardSlots" :key="slot.name")
                td.font-weight-medium.text-no-wrap {{ slot.name }}
                td.single-line {{ slot.description }}
</template>

<script>
export default {
  data() {
    return {
      copiedCode: null,
      variationFiles: {
        'basic-card': [],
        'multiple-files': [],
        'custom-styling': [],
        'document-upload': [],
        'elevated-card': [],
        'append-files': [],
        'custom-drag-colors': [],
        'immediate-upload': []
      },
      slotFiles: {
        'custom-icon': [],
        'profile-upload': [],
        'document-card': []
      },
      uploadVariations: [
        {
          name: 'Basic Upload Card',
          id: 'basic-card',
          description: 'A simple upload card with default settings',
          props: {},
          code: 'yz-upload-card(v-model="files")'
        },
        {
          name: 'Multiple Files Card',
          id: 'multiple-files',
          description: 'Allow selecting multiple files at once',
          props: {
            multiple: true,
            primaryTitle: 'Click to upload multiple files',
            description: 'Select multiple files to upload'
          },
          code: `yz-upload-card(
  multiple
  primary-title="Click to upload multiple files"
  description="Select multiple files to upload"
  v-model="files"
)`
        },
        {
          name: 'Custom Styling Card',
          id: 'custom-styling',
          description: 'Upload card with custom dimensions and styling',
          props: {
            height: 250,
            width: 350,
            icon: 'mdi-camera',
            iconColor: 'purple',
            primaryTitle: 'Photo Upload',
            secondaryTitle: 'or image files',
            description: 'Upload your photos here',
            accept: 'image/*'
          },
          code: `yz-upload-card(
  :height="250"
  :width="350"
  icon="mdi-camera"
  icon-color="purple"
  primary-title="Photo Upload"
  secondary-title="or image files"
  description="Upload your photos here"
  accept="image/*"
  v-model="files"
)`
        },
        {
          name: 'Document Upload',
          id: 'document-upload',
          description: 'Upload card specifically for documents',
          props: {
            icon: 'mdi-file-document',
            iconColor: 'blue',
            primaryTitle: 'Document Upload',
            description: 'Upload PDF, DOC, or DOCX files',
            accept: '.pdf,.doc,.docx'
          },
          code: `yz-upload-card(
  icon="mdi-file-document"
  icon-color="blue"
  primary-title="Document Upload"
  description="Upload PDF, DOC, or DOCX files"
  accept=".pdf,.doc,.docx"
  v-model="files"
)`
        },
        {
          name: 'Elevated Card',
          id: 'elevated-card',
          description: 'Upload card with elevation shadow',
          props: {
            elevation: 3,
            outlined: false,
            icon: 'mdi-cloud-upload',
            iconColor: 'success',
            primaryTitle: 'Cloud Upload',
            secondaryTitle: 'or files from your device',
            description: 'Upload files to the cloud'
          },
          code: `yz-upload-card(
  :elevation="3"
  :outlined="false"
  icon="mdi-cloud-upload"
  icon-color="success"
  primary-title="Cloud Upload"
  secondary-title="or files from your device"
  description="Upload files to the cloud"
  v-model="files"
)`
        },
        {
          name: 'Append Files Mode',
          id: 'append-files',
          description: 'Multiple file upload that appends new files to existing ones',
          props: {
            multiple: true,
            appendFiles: true,
            primaryTitle: 'Add More Files',
            secondaryTitle: 'or drag and drop',
            description: 'Additional files will be added to existing selection',
            icon: 'mdi-plus-circle',
            iconColor: 'secondary'
          },
          code: `yz-upload-card(
  multiple
  append-files
  primary-title="Add More Files"
  secondary-title="or drag and drop"
  description="Additional files will be added to existing selection"
  icon="mdi-plus-circle"
  icon-color="secondary"
  v-model="files"
)`
        },
        {
          name: 'Custom Drag Colors',
          id: 'custom-drag-colors',
          description: 'Upload card with custom drag-over colors',
          props: {
            primaryTitle: 'Drag Files Here',
            description: 'Custom drag-over styling',
            dragOverBackgroundColor: '#e8f5e8',
            dragOverBorderColor: '#4caf50',
            icon: 'mdi-drag',
            iconColor: 'success'
          },
          code: `yz-upload-card(
  primary-title="Drag Files Here"
  description="Custom drag-over styling"
  drag-over-background-color="#e8f5e8"
  drag-over-border-color="#4caf50"
  icon="mdi-drag"
  icon-color="success"
  v-model="files"
)`
        },
        {
          name: 'Immediate Upload Mode',
          id: 'immediate-upload',
          description: 'Upload files immediately to API endpoint with real-time progress tracking',
          props: {
            uploadMode: 'immediate',
            primaryTitle: 'Upload to Server',
            description: 'Files will be uploaded immediately',
            icon: 'mdi-cloud-upload',
            iconColor: 'primary',
            multiple: true,
            appendFiles: true,
          },
          code: `yz-upload-card(
  upload-mode="immediate"
  upload-url="/documents/"
  primary-title="Upload to Server"
  description="Files will be uploaded immediately"
  icon="mdi-cloud-upload"
  icon-color="primary"
  multiple,
  append-files,
  @upload-success="handleUploadSuccess"
  @upload-error="handleUploadError"
  v-model="files"
)`
        }
      ],
      slotExamples: [
        {
          name: 'Custom Icon Slot',
          id: 'custom-icon',
          description: 'Using custom content in the icon slot',
          code: `yz-upload-card(v-model="files")
  template(#icon)
    yz-icon(size="48" color="success") mdi-image-multiple
  template(#title)
    .text-h6.text--primary Gallery Upload
  template(#description)
    .text-body-2.text--secondary Add images to your gallery`
        },
        {
          name: 'Profile Upload Card',
          id: 'profile-upload',
          description: 'Customized card for profile picture upload',
          code: `yz-upload-card(v-model="files" accept="image/*" :multiple="false")
  template(#icon)
    yz-icon(size="56" color="primary") mdi-account-circle
  template(#title)
    .text-h6.text--primary Profile Picture
  template(#description)
    .text-body-2.text--secondary Upload your profile picture (PNG, JPG)`
        },
        {
          name: 'Document Card with Slots',
          id: 'document-card',
          description: 'Document upload with custom slot content',
          code: `yz-upload-card(v-model="files" accept=".pdf,.doc,.docx")
  template(#icon)
    yz-icon(size="52" color="orange") mdi-file-document
  template(#title)
    .text-h6.text--primary Document Upload
  template(#description)
    .text-body-2.text--secondary Upload PDF, DOC, or DOCX files`
        }
      ],
      uploadCardProps: [
        {
          name: 'value',
          type: 'Array',
          default: '[]',
          description: 'Array of uploaded files with blob and file properties (v-model)'
        },
        {
          name: 'multiple',
          type: 'Boolean',
          default: 'false',
          description: 'Allow multiple file selection'
        },
        {
          name: 'accept',
          type: 'String',
          default: 'image/*',
          description: 'File types accepted by the file input'
        },
        {
          name: 'appendFiles',
          type: 'Boolean',
          default: 'false',
          description: 'When true and multiple is true, append new files to existing v-model instead of replacing them'
        },
        {
          name: 'icon',
          type: 'String',
          default: '$yzi-image-01',
          description: 'Icon to display in the card'
        },
        {
          name: 'iconSize',
          type: 'String | Number',
          default: '18',
          description: 'Size of the icon'
        },
        {
          name: 'iconColor',
          type: 'String',
          default: 'primary',
          description: 'Color of the icon'
        },
        {
          name: 'iconContainerSize',
          type: 'String | Number',
          default: '30',
          description: 'Size of the icon container (width and height)'
        },
        {
          name: 'primaryTitle',
          type: 'String',
          default: 'Click to upload',
          description: 'Primary title text displayed in the card'
        },
        {
          name: 'secondaryTitle',
          type: 'String',
          default: 'or drag and drop',
          description: 'Secondary title text displayed next to primary title'
        },
        {
          name: 'description',
          type: 'String',
          default: 'JPG or PNG',
          description: 'Description text displayed in the card'
        },
        {
          name: 'disabled',
          type: 'Boolean',
          default: 'false',
          description: 'Disable the upload card'
        },
        {
          name: 'loading',
          type: 'Boolean',
          default: 'false',
          description: 'Show loading state'
        },
        {
          name: 'height',
          type: 'String | Number',
          default: 'null',
          description: 'Height of the card'
        },
        {
          name: 'width',
          type: 'String | Number',
          default: 'null',
          description: 'Width of the card'
        },
        {
          name: 'minHeight',
          type: 'String | Number',
          default: '200',
          description: 'Minimum height of the card'
        },
        {
          name: 'outlined',
          type: 'Boolean',
          default: 'true',
          description: 'Use outlined card style with dashed border'
        },
        {
          name: 'elevation',
          type: 'String | Number',
          default: '0',
          description: 'Elevation level for card shadow (0-5)'
        },
        {
          name: 'dragOverBackgroundColor',
          type: 'String',
          default: 'blue-100',
          description: 'Background color when files are dragged over the card. Supports hex colors (#fff), CSS variables (var(--color)), YZ design system colors (primary-50), or custom variables (yz-custom-color)'
        },
        {
          name: 'dragOverBorderColor',
          type: 'String',
          default: 'blue-500',
          description: 'Border color when files are dragged over the card. Supports hex colors (#fff), CSS variables (var(--color)), YZ design system colors (primary-500), or custom variables (yz-custom-color)'
        },
        {
          name: 'uploadMode',
          type: 'String',
          default: 'preview',
          description: 'Upload strategy: "preview" (create blob URLs) or "immediate" (upload to API immediately)'
        },
        {
          name: 'uploadUrl',
          type: 'String',
          default: '/documents/',
          description: 'API endpoint for immediate upload mode'
        },
        {
          name: 'uploadHeaders',
          type: 'Object',
          default: '{}',
          description: 'Custom headers for upload requests in immediate mode'
        },
        {
          name: 'uploadMethod',
          type: 'String',
          default: 'POST',
          description: 'HTTP method for upload requests in immediate mode'
        }
      ],
      uploadCardEvents: [
        {
          name: 'input',
          payload: 'Array',
          description: 'Emitted when files are uploaded for v-model binding, contains array of {blob, file} objects'
        },
        {
          name: 'error',
          payload: 'Error',
          description: 'Emitted when an error occurs during file processing'
        },
        {
          name: 'upload-start',
          payload: '{file, fileId}',
          description: 'Emitted when upload begins in immediate mode'
        },
        {
          name: 'upload-progress',
          payload: '{file, fileId, progress}',
          description: 'Emitted during upload with progress percentage in immediate mode'
        },
        {
          name: 'upload-success',
          payload: '{file, fileId, response}',
          description: 'Emitted when upload completes successfully in immediate mode'
        },
        {
          name: 'upload-error',
          payload: '{file, fileId, error}',
          description: 'Emitted when upload fails in immediate mode'
        }
      ],
      uploadCardSlots: [
        {
          name: 'icon',
          description: 'Customize the icon content in the upload card. Replaces the entire icon section including the circular container.'
        },
        {
          name: 'title',
          description: 'Customize the title content in the upload card. Replaces both primaryTitle and secondaryTitle props.'
        },
        {
          name: 'description',
          description: 'Customize the description content in the upload card. Replaces the description prop.'
        }
      ],
      customDragExample: `yz-upload-card(
  primary-title="Drag Files Here"
  description="Custom drag-over styling"
  drag-over-background-color="success-50"
  drag-over-border-color="success-500"
  icon="mdi-drag"
  icon-color="success"
  v-model="files"
)

// Alternative with hex colors:
yz-upload-card(
  drag-over-background-color="#e8f5e8"
  drag-over-border-color="#4caf50"
  v-model="files"
)

// Alternative with CSS variables:
yz-upload-card(
  drag-over-background-color="var(--my-bg-color)"
  drag-over-border-color="var(--my-border-color)"
  v-model="files"
)`
    }
  },
  computed: {
    basicCardFiles: {
      get() {
        return this.variationFiles['basic-card']
      },
      set(value) {
        this.$set(this.variationFiles, 'basic-card', value)
      }
    },
    multipleFilesFiles: {
      get() {
        return this.variationFiles['multiple-files']
      },
      set(value) {
        this.$set(this.variationFiles, 'multiple-files', value)
      }
    },
    customStylingFiles: {
      get() {
        return this.variationFiles['custom-styling']
      },
      set(value) {
        this.$set(this.variationFiles, 'custom-styling', value)
      }
    },
    documentUploadFiles: {
      get() {
        return this.variationFiles['document-upload']
      },
      set(value) {
        this.$set(this.variationFiles, 'document-upload', value)
      }
    },
    elevatedCardFiles: {
      get() {
        return this.variationFiles['elevated-card']
      },
      set(value) {
        this.$set(this.variationFiles, 'elevated-card', value)
      }
    },
    appendFilesFiles: {
      get() {
        return this.variationFiles['append-files']
      },
      set(value) {
        this.$set(this.variationFiles, 'append-files', value)
      }
    },
    customDragColorsFiles: {
      get() {
        return this.variationFiles['custom-drag-colors']
      },
      set(value) {
        this.$set(this.variationFiles, 'custom-drag-colors', value)
      }
    },
    immediateUploadFiles: {
      get() {
        return this.variationFiles['immediate-upload']
      },
      set(value) {
        this.$set(this.variationFiles, 'immediate-upload', value)
      }
    },
    customIconFiles: {
      get() {
        return this.slotFiles['custom-icon']
      },
      set(value) {
        this.$set(this.slotFiles, 'custom-icon', value)
      }
    },
    profileUploadFiles: {
      get() {
        return this.slotFiles['profile-upload']
      },
      set(value) {
        this.$set(this.slotFiles, 'profile-upload', value)
      }
    },
    documentCardFiles: {
      get() {
        return this.slotFiles['document-card']
      },
      set(value) {
        this.$set(this.slotFiles, 'document-card', value)
      }
    }
  },
  mounted() {
    this.$nextTick(() => {
      this.scrollToSection()
    })
  },
  watch: {
    '$route.hash': {
      handler() {
        this.scrollToSection()
      },
      immediate: false
    }
  },
  methods: {
    copyCode(code) {
      navigator.clipboard.writeText(code).then(() => {
        this.copiedCode = code
        setTimeout(() => {
          this.copiedCode = null
        }, 2000)
      })
    },
    navigateToSection(sectionId) {
      this.$router.push({
        name: this.$route.name,
        hash: `#${sectionId}`,
        query: this.$route.query
      })
    },
    scrollToSection() {
      const sectionIdWithHash = this.$route.hash
      const sectionId = sectionIdWithHash.replace('#', '')
      if (sectionId) {
        this.$nextTick(() => {
          const element = document.getElementById(sectionId)
          if (element) {
            element.scrollIntoView({
              behavior: 'smooth',
              block: 'start'
            })
          }
        })
      }
    },
    handleUploadError(error) {
      console.error('Upload error:', error)
    },
    handleUploadSuccess(data) {
      console.log('Upload success:', data)
    },
    handleUploadProgress(data) {
      console.log('Upload progress:', data)
    },
    formatModelData(files) {
      if (!files || !files.length) return '[]'

      const truncatedFiles = files.map(file => ({
        ...file,
        blob: file.blob ? file.blob.substring(0, 50) + '...' : null,
        file: file.file ? "<file_object>" : undefined,
        uploadResponse: file.uploadResponse ? "<upload_response_object>" : undefined
      }))

      return JSON.stringify(truncatedFiles, null, 2)
    }
  }
}
</script>

<style scoped>
.variations-grid {
  max-width: 100%;
}

.variation-item {
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 20px;
  background-color: #ffffff;
}

.variation-preview {
  background-color: #fafafa;
  border: 1px dashed #d0d0d0 !important;
}

.code-block {
  background-color: #f8f9fa !important;
  border: 1px solid #e9ecef;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  line-height: 1.5;
}

.code-header {
  border-bottom: 1px solid #e0e0e0;
  padding-bottom: 8px;
}

.component-section {
  background-color: #ffffff;
  border-radius: 8px;
  border: 1px solid #e0e0e0;
  padding: 24px;
}

.props-table, .events-table, .slots-table {
  overflow-x: auto;
}

.props-table .v-data-table,
.events-table .v-data-table,
.slots-table .v-data-table {
  border: 1px solid #e0e0e0;
}

.single-line {
  white-space: normal;
  word-wrap: break-word;
  max-width: 500px;
}

.text-no-wrap {
  white-space: nowrap;
}

.cursor-pointer {
  cursor: pointer;
  transition: color 0.2s ease;
}

.cursor-pointer:hover {
  color: #1976d2;
}

</style>